import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { Form, FormControl, FormField, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { validateProfessionalEmail } from "@/utils/emailValidation";
import { ProfilePictureUpload } from "./ProfilePictureUpload";

const accountSchema = z.object({
  first_name: z.string().min(1, "Le prénom est obligatoire"),
  last_name: z.string().min(1, "Le nom est obligatoire"),
  email: z.string()
    .email("Format d'email invalide")
    .refine(validateProfessionalEmail, "Veuillez utiliser une adresse email professionnelle"),
  company: z.string().min(1, "L'entreprise est obligatoire"),
  position: z.string().min(1, "Le poste est obligatoire"),
});

type AccountFormData = z.infer<typeof accountSchema>;

export function AccountSettings() {
  const { profile, updateProfile, refreshProfile } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = React.useState(false);

  const form = useForm<AccountFormData>({
    resolver: zodResolver(accountSchema),
    defaultValues: {
      first_name: profile?.first_name || "",
      last_name: profile?.last_name || "",
      email: profile?.email || "",
      company: profile?.company || "",
      position: profile?.position || "",
    },
  });

  React.useEffect(() => {
    if (profile) {
      form.reset({
        first_name: profile.first_name,
        last_name: profile.last_name,
        email: profile.email,
        company: profile.company,
        position: profile.position,
      });
    }
  }, [profile, form]);

  const onSubmit = async (data: AccountFormData) => {
    setIsLoading(true);
    try {
      const { error } = await updateProfile(data);
      
      if (error) {
        toast({
          title: "Erreur",
          description: "Impossible de mettre à jour le profil. Veuillez réessayer.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Succès",
          description: "Votre profil a été mis à jour avec succès.",
        });
      }
    } catch (_error) {
      toast({
        title: "Erreur",
        description: "Une erreur inattendue s'est produite.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAvatarUpdate = async (avatarUrl: string | null) => {
    // Rafraîchir le profil pour mettre à jour l'avatar dans toute l'app
    await refreshProfile();
  };

  return (
    <div className="w-full inline-flex flex-col justify-start items-start gap-6">
      <div className="self-stretch flex flex-col justify-start items-start gap-1">
        <div className="self-stretch justify-start text-gray-800 text-2xl font-medium font-['Bricolage_Grotesque']">
          Informations du profil
        </div>
        <div className="justify-center text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque']">
          Mettez à jour vos informations personnelles et professionnelles
        </div>
      </div>

      {/* Photo de profil */}
      <div className="w-full">
        <ProfilePictureUpload 
          currentAvatarUrl={profile?.avatar_url}
          onAvatarUpdate={handleAvatarUpdate}
        />
      </div>
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="w-full space-y-5">
          {/* Prénom et Nom - 2 colonnes 50% chacune */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <FormField
              control={form.control}
              name="first_name"
              render={({ field }) => (
                <div className="w-full flex flex-col gap-2">
                  <FormLabel className="text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque']">
                    Prénom
                  </FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="Dimitri" 
                      {...field} 
                      className="w-full px-3 py-4 h-12 bg-white rounded-lg border border-gray-300 text-gray-800 text-sm font-medium font-['Bricolage_Grotesque'] focus:border-teal-400 focus:ring-1 focus:ring-teal-400 focus:outline-none"
                    />
                  </FormControl>
                  <FormMessage />
                </div>
              )}
            />

            <FormField
              control={form.control}
              name="last_name"
              render={({ field }) => (
                <div className="w-full flex flex-col gap-2">
                  <FormLabel className="text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque']">
                    Nom
                  </FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="NDEKE" 
                      {...field} 
                      className="w-full px-3 py-4 h-12 bg-white rounded-lg border border-gray-300 text-gray-800 text-sm font-medium font-['Bricolage_Grotesque'] focus:border-teal-400 focus:ring-1 focus:ring-teal-400 focus:outline-none"
                    />
                  </FormControl>
                  <FormMessage />
                </div>
              )}
            />
          </div>

          {/* Email - 100% largeur */}
          <div className="w-full">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <div className="w-full flex flex-col gap-2">
                  <FormLabel className="text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque']">
                    Adresse email
                  </FormLabel>
                  <FormControl>
                    <Input 
                      type="email" 
                      placeholder="dimitri@leaders-os.com" 
                      {...field} 
                      className="w-full px-3 py-4 h-12 bg-white rounded-lg border border-gray-300 text-gray-800 text-sm font-medium font-['Bricolage_Grotesque'] focus:border-teal-400 focus:ring-1 focus:ring-teal-400 focus:outline-none"
                    />
                  </FormControl>
                  <FormMessage />
                </div>
              )}
            />
          </div>

          {/* Entreprise et Poste - 2 colonnes 50% chacune */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <FormField
              control={form.control}
              name="company"
              render={({ field }) => (
                <div className="w-full flex flex-col gap-2">
                  <FormLabel className="text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque']">
                    Entreprise
                  </FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="Leaders OS" 
                      {...field} 
                      className="w-full px-3 py-4 h-12 bg-white rounded-lg border border-gray-300 text-gray-800 text-sm font-medium font-['Bricolage_Grotesque'] focus:border-teal-400 focus:ring-1 focus:ring-teal-400 focus:outline-none"
                    />
                  </FormControl>
                  <FormMessage />
                </div>
              )}
            />

            <FormField
              control={form.control}
              name="position"
              render={({ field }) => (
                <div className="w-full flex flex-col gap-2">
                  <FormLabel className="text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque']">
                    Poste
                  </FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="CEO" 
                      {...field} 
                      className="w-full px-3 py-4 h-12 bg-white rounded-lg border border-gray-300 text-gray-800 text-sm font-medium font-['Bricolage_Grotesque'] focus:border-teal-400 focus:ring-1 focus:ring-teal-400 focus:outline-none"
                    />
                  </FormControl>
                  <FormMessage />
                </div>
              )}
            />
          </div>

          <Button 
            type="submit" 
            disabled={isLoading || !form.formState.isDirty}
            className="h-11 px-4 py-3 bg-teal-400 rounded-lg text-white font-semibold font-['Bricolage_Grotesque'] hover:bg-teal-500 border-0"
          >
            {isLoading ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </form>
      </Form>
    </div>
  );
}